import type { ImageElement } from '@/components/canvas/document/publicTypes';
import { resolveCornerRadiiPx } from '@/components/canvas/shared/utils/geometry/cornerRadius';
import { loadImage } from '@/components/canvas/rendering/export/loadImage';
import { roundedRectPath } from '@/components/canvas/rendering/export/roundedRect';
import type { GetImageProxyUrl } from '@/components/canvas/rendering/export/types';
import { firstShadow, firstStroke } from '@/components/canvas/elements/model';

// ─── image ────────────────────────────────────────────────────────────────────

/**
 * 与 ImageElement / Workspace 一致：同时有 X/Y 时优先；否则解析 `x% y%` 或 center。
 * 返回 0–1 的锚点比例，用于 cover/contain 裁切偏移（同画布背景 drawBackground 中 imagePosition）。
 */
function resolveImageObjectPositionFraction(el: ImageElement): { px: number; py: number } {
  const position = el.data.objectPosition;
  if (position) {
    return {
      px: Math.min(1, Math.max(0, position.x / 100)),
      py: Math.min(1, Math.max(0, position.y / 100)),
    };
  }
  return { px: 0.5, py: 0.5 };
}

export async function drawImage(ctx: CanvasRenderingContext2D, el: ImageElement, getImageProxyUrl?: (src: string) => Promise<string | null>) {
  const { x, y } = el.layout;
  const { width, height } = el.size;
  const { src, objectFit = 'cover' } = el.data;
  const shadow = firstShadow(el.appearance);
  const stroke = firstStroke(el.appearance);

  try {
    const img = await loadImage(src, getImageProxyUrl);

    if (shadow) {
      ctx.shadowBlur = shadow.blur;
      ctx.shadowColor = shadow.color;
      ctx.shadowOffsetX = shadow.offsetX;
      ctx.shadowOffsetY = shadow.offsetY;
    }

    ctx.save();

    // 与编辑器内层 div（overflow-hidden）一致：必须始终按容器裁剪，否则 cover + imageScale 时绘制会超出边界
    const r = resolveCornerRadiiPx(el);
    ctx.clip(roundedRectPath(x, y, width, height, r));

    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    const pos = resolveImageObjectPositionFraction(el);
    const { sx, sy, sw, sh, dx, dy, dw, dh } = computeObjectFit(
      img.width,
      img.height,
      x,
      y,
      width,
      height,
      objectFit,
      pos,
    );

    const imageScale = el.data.imageScale ?? 1;
    if (imageScale !== 1 && imageScale > 0 && Number.isFinite(imageScale)) {
      const Ox = x + width * pos.px;
      const Oy = y + height * pos.py;
      ctx.translate(Ox, Oy);
      ctx.scale(imageScale, imageScale);
      ctx.translate(-Ox, -Oy);
    }

    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);

    ctx.restore();

    // border (drawn after image, outside clip)
    if (stroke && stroke.width > 0 && stroke.color) {
      const path = roundedRectPath(x, y, width, height, r);
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      if (stroke.style === 'dashed') ctx.setLineDash([stroke.width * 3, stroke.width * 2]);
      else if (stroke.style === 'dotted') ctx.setLineDash([stroke.width, stroke.width]);
      else ctx.setLineDash([]);
      ctx.stroke(path);
      ctx.setLineDash([]);
    }
  } catch {
    // image failed to load: draw placeholder
    ctx.fillStyle = '#e5e5e5';
    ctx.fillRect(x, y, width, height);
    ctx.strokeStyle = '#aaa';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, width, height);
  }
}

function computeObjectFit(
  imgW: number,
  imgH: number,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  objectFit: string,
  pos: { px: number; py: number } = { px: 0.5, py: 0.5 },
): { sx: number; sy: number; sw: number; sh: number; dx: number; dy: number; dw: number; dh: number } {
  if (objectFit === 'fill') {
    return { sx: 0, sy: 0, sw: imgW, sh: imgH, dx, dy, dw, dh };
  }
  if (objectFit === 'none') {
    const sw = Math.min(imgW, dw),
      sh = Math.min(imgH, dh);
    return { sx: 0, sy: 0, sw, sh, dx: dx + (dw - sw) / 2, dy: dy + (dh - sh) / 2, dw: sw, dh: sh };
  }
  const imgRatio = imgW / imgH;
  const pageAspectRatio = dw / dh;
  if (objectFit === 'contain' || (objectFit === 'scale-down' && imgW < dw && imgH < dh)) {
    if (imgRatio > pageAspectRatio) {
      const newH = dw / imgRatio;
      return {
        sx: 0,
        sy: 0,
        sw: imgW,
        sh: imgH,
        dx,
        dy: dy + (dh - newH) * pos.py,
        dw,
        dh: newH,
      };
    }
    const newW = dh * imgRatio;
    return {
      sx: 0,
      sy: 0,
      sw: imgW,
      sh: imgH,
      dx: dx + (dw - newW) * pos.px,
      dy,
      dw: newW,
      dh,
    };
  }
  // cover（及 scale-down 在图大于容器时与 cover 相同）
  if (imgRatio > pageAspectRatio) {
    const sh = imgH,
      sw = Math.round(imgH * pageAspectRatio),
      sx = Math.round((imgW - sw) * pos.px);
    return { sx, sy: 0, sw, sh, dx, dy, dw, dh };
  }
  const sw = imgW,
    sh = Math.round(imgW / pageAspectRatio),
    sy = Math.round((imgH - sh) * pos.py);
  return { sx: 0, sy, sw, sh, dx, dy, dw, dh };
}
